<script setup lang="ts">
import { equipCollection } from '@/core/equip'
import type { EquipItemType, MakeDivineArmsItem } from '@/types/equip'
import { getEquipName } from '@/utils/equip'
import { ref } from 'vue'
import EquipInfo from '@/views/item/components/EquipInfo.vue'
import { equipIcon } from '@/views/item/data'
import { DArrowLeft, Plus, QuestionFilled } from '@element-plus/icons-vue'
import { onMounted } from 'vue'
import { socketEmitAsync } from '@/utils/web-socket'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/stores/modules/user'
import type { UserAttrType } from '@/types/user'
import { formatNumberWithCommas } from '@/utils'

const userStore = useUserStore()

const makeDivineArmsList = ref<MakeDivineArmsItem[]>()
const equipInfo = ref<EquipItemType>()
const useEquipInfo = ref<EquipItemType>()
const resEquipInfo = ref<EquipItemType>()
const itemList = ref<EquipItemType[]>()
const makeShow = ref(false)

function putInEquip() {
  const makeId = equipInfo.value?.id
  if (!makeId) return
  const makeItem = makeDivineArmsList.value?.find((item) => item.id === Number(makeId))
  if (!makeItem) return ElMessage.warning('制作的商品不存在')
  const useEquip = userStore.itemList.find((item) => item.equipId === makeItem.baseEquipId && item.starAttrs?.length === 5)
  if (!useEquip) return ElMessage.warning('您未拥有对应条件的装备')
  const equip = equipCollection[useEquip.equipId]
  useEquipInfo.value = {
    ...useEquip,
    name: equip.name,
    attrs: equip.attrs,
    type: equip.type,
  }
  resEquipInfo.value = {
    ...equipInfo.value!,
    reinforcedLevel: useEquipInfo.value.reinforcedLevel,
    starAttrs: useEquipInfo.value.starAttrs,
  }
  makeShow.value = true
}
async function makeDivineArm() {
  if (userStore.consumables.antiquityStone < 8) return ElMessage.warning('远古石不足')
  if (userStore.gold < 3000000) return ElMessage.warning('金币不足')
  const makeId = Number(equipInfo.value?.id)
  const useId = useEquipInfo.value?.id
  if (!makeId || !useId || !equipInfo.value) return

  ElMessageBox.confirm(`<p class="affix">确定打造【<span class="antiquity">${equipInfo.value?.name}</span>】吗？`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      const res = await socketEmitAsync<UserAttrType>({ type: 'makeDivineArm', data: { makeId, useId } })
      const item = userStore.itemList.find((item) => item.id === useId)
      if (!item) return
      item.equipId = equipInfo.value!.equipId
      item.quality = 'antiquity'

      userStore.consumables.antiquityStone -= 8
      userStore.gold -= 3000000
      userStore.userAttr = res

      makeShow.value = false
    })
    .catch(() => {})
}

onMounted(async () => {
  const res = await socketEmitAsync<MakeDivineArmsItem[]>({ type: 'getMakeDivineArmsList' })
  makeDivineArmsList.value = res
  itemList.value = res.map((item) => {
    const equip = equipCollection[item.equipId]
    return {
      id: String(item.id),
      equipId: item.equipId,
      ...equip,
      quality: equip.quality[equip.quality.length - 1]!,
      reinforcedLevel: 0,
      isLock: false,
    }
  })
})
</script>

<template>
  <div class="divine-arms">
    <div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px">
      <div>
        <span style="margin-right: 10px">远古石：{{ userStore.consumables.antiquityStone }}</span>
        <span>💰 {{ formatNumberWithCommas(userStore.gold) }}</span>
      </div>
      <div class="help-info">
        <el-popover placement="bottom" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>神兵说明</h4>
            <p>1. 通过<span class="antiquity">不朽之塔</span>的挑战，您可以获得<span class="antiquity">远古石</span>，用于神兵的打造。</p>
            <p>2. 打造时，您需要按照提示放入对应的<span class="info">满星</span>装备。</p>
            <p>
              3. 神兵的打造主要改变原装备的品质，会保留<span class="info">强化等级</span>及<span class="info">升星等级</span
              >，当满足条件时，请放心打造。
            </p>
          </div>
        </el-popover>
      </div>
    </div>
    <div class="main">
      <div class="list border-wrap">
        <h4>神兵</h4>
        <div class="arms-list item-btn-wrap btn-wrap">
          <CommonBtn
            v-for="item of itemList"
            :key="item.equipId"
            size="small"
            @done="equipInfo = item"
            :style="{ position: 'relative', borderColor: equipInfo?.equipId === item.equipId ? 'red' : '' }"
          >
            <span v-html="getEquipName(item, item.type)"></span>
          </CommonBtn>
        </div>
      </div>
      <div class="detail">
        <div class="make border-wrap">
          <h4>打造</h4>
          <template v-if="equipInfo">
            <div class="make-way">
              <p>
                <b style="color: #884898">★</b> <span class="legend">{{ equipIcon[equipInfo.type] }} {{ equipInfo.name.replace('真 · ', '') }}</span>
              </p>
              <p>
                <el-icon><Plus /></el-icon>
              </p>
              <p><span class="antiquity">远古石</span> * 8</p>
              <p>💰 3,000,000</p>
              <p>
                <el-button size="small" type="success" plain :icon="Plus" @click="putInEquip">放入装备</el-button>
              </p>
            </div>
          </template>
          <p style="text-align: center" v-else>（请选择装备）</p>
        </div>
        <div class="info border-wrap">
          <h4>装备信息</h4>
          <template v-if="equipInfo">
            <EquipInfo :equipInfo="equipInfo" :showAction="false" />
          </template>
        </div>
      </div>
    </div>

    <el-dialog modal-class="make-confirm" v-model="makeShow" width="100%" style="max-width: 500px">
      <h2>打造确认</h2>
      <p>
        <template v-if="useEquipInfo">
          <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
            <template #reference>
              <el-button size="small">
                <span v-html="getEquipName(useEquipInfo, useEquipInfo.type)"></span>
              </el-button>
            </template>
            <EquipInfo :equip-info="useEquipInfo" :show-action="false" />
          </el-popover>
        </template>
      </p>
      <p>
        <el-icon size="25" style="transform: rotate(-90deg)"><DArrowLeft /></el-icon>
      </p>
      <p>
        <template v-if="resEquipInfo">
          <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
            <template #reference>
              <el-button size="small">
                <span v-html="getEquipName(resEquipInfo, resEquipInfo.type)"></span>
              </el-button>
            </template>
            <EquipInfo :equip-info="resEquipInfo" :show-action="false" />
          </el-popover>
        </template>
      </p>
      <p style="margin-top: 20px">材料需求：</p>
      <p><span class="antiquity">远古石</span> * 8</p>
      <p>💰 3,000,000</p>
      <p style="margin-top: 20px">
        <el-button type="success" plain @click="makeDivineArm">确认打造</el-button>
      </p>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="makeShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.divine-arms {
  flex-flow: column nowrap;
  .main {
    flex: 1;
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    overflow: hidden;
    .border-wrap {
      padding: 0 10px 10px 10px;
    }
    .list {
      width: calc(50% - 5px);
      display: flex;
      flex-flow: column nowrap;
      .arms-list {
        overflow: auto;
      }
      :deep() {
        .el-button:focus,
        .el-button:hover {
          background-color: unset;
        }
      }
    }
    .detail {
      width: calc(50% - 5px);
      display: flex;
      flex-flow: column nowrap;
      overflow: hidden;
      .make {
        margin-bottom: 10px;
        p {
          font-size: 14px;
          margin: 4px 0;
        }
        .make-way {
          text-align: center;
        }
      }
      .info {
        flex: 1;
        overflow: hidden;
        display: flex;
        flex-flow: column;
      }
    }
    .btn-wrap {
      > div {
        margin-bottom: 10px;
      }
      > div:last-child {
        margin-bottom: 0;
      }
    }
  }
  .make-confirm {
    p {
      text-align: center;
      margin: 5px 0;
    }
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
  }
}
</style>
